// Global ₹-format toggle. Three modes:
//
//   "full" — default. fmtRupees renders full Indian digits; fmtRupeesShort
//            auto-shortens to L / cr / plain based on scale for narrow tiles.
//   "l"    — Every ₹ renders in Lakhs (₹86.17 L). Constant unit.
//   "mn"   — Every ₹ renders in Millions (₹8.62 Mn). Constant unit.
//
// Persisted in localStorage so the choice survives reloads. A pure
// `getCurrentFormatMode()` fallback lets non-React code (formatter
// pure functions in fmt.ts) read the current mode synchronously.
//
// Legacy migration on read: pre-#141 values `"short"` / `"indian"`
// migrate to `"l"`, and `"international"` migrates to `"mn"`.
//
// Auto-refresh on toggle: the Provider exports a `<FormatSubscriber>`
// wrapper. Any page tree wrapped in it re-renders when the mode
// changes, so displays refresh without a manual page reload. See
// `app/(app)/layout.tsx` for the placement.
//
// Per chakra_dashboard#11, extended by #141.

"use client";

import { createContext, useContext, useEffect, useState } from "react";

export type FormatMode = "full" | "l" | "mn";
const LS_KEY = "chakra:format:v1";

/** Coerce a raw localStorage value (possibly stale/legacy) into a valid
 *  FormatMode. Legacy migrations:
 *    "short"         → "l"    (pre-#141 2-mode "short" was L/cr)
 *    "indian"        → "l"    (interim 3-mode L·Cr slug)
 *    "international" → "mn"   (interim 3-mode Mn·Bn slug)
 *  Anything unrecognized (or missing) → `DEFAULT_MODE`.
 */
const DEFAULT_MODE: FormatMode = "l";  // L is the primary desk view

function coerce(raw: string | null): FormatMode {
  if (raw === "full" || raw === "l" || raw === "mn") return raw;
  if (raw === "short" || raw === "indian") return "l";
  if (raw === "international") return "mn";
  return DEFAULT_MODE;
}

const FormatContext = createContext<{
  mode: FormatMode;
  setMode: (m: FormatMode) => void;
}>({
  mode: DEFAULT_MODE,
  setMode: () => {},
});

export function NumberFormatProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = useState<FormatMode>(DEFAULT_MODE);

  useEffect(() => {
    try {
      setModeState(coerce(window.localStorage.getItem(LS_KEY)));
    } catch {
      /* localStorage unavailable */
    }
  }, []);

  const setMode = (m: FormatMode) => {
    setModeState(m);
    try {
      window.localStorage.setItem(LS_KEY, m);
    } catch {
      /* no-op */
    }
  };

  return (
    <FormatContext.Provider value={{ mode, setMode }}>
      {children}
    </FormatContext.Provider>
  );
}

export function useNumberFormat() {
  return useContext(FormatContext);
}

/** Wrap page content in this to re-render on mode change.
 *
 *  Implementation: consume the format context AND key the wrapping
 *  <div> on `mode`. A key change forces React to unmount the previous
 *  subtree and mount a fresh one — every page's `useState` /
 *  `useEffect` reinitializes, and every `fmtRupees(...)` call in the
 *  rendered JSX runs fresh against the new mode.
 *
 *  Why the sledgehammer: relying on context-consumer re-renders alone
 *  did NOT propagate through pages that hold derived data in
 *  `useMemo` / `useState` — the memoized values didn't re-derive
 *  because their inputs (row data) hadn't changed, even though the
 *  display format had. Keying the wrapper on `mode` sidesteps all
 *  that: mode changes → subtree remounts → everything renders anew.
 *
 *  Trade-off: pages lose ephemeral state on toggle (scroll position,
 *  in-progress input on a form). Acceptable for a global format
 *  switch, which the operator flips deliberately. */
export function FormatSubscriber({ children }: { children: React.ReactNode }) {
  const { mode } = useContext(FormatContext);
  return <div key={mode}>{children}</div>;
}

/** Synchronous read for non-React callers (formatter pure functions,
 *  export helpers). Returns `DEFAULT_MODE` if window is undefined (SSR)
 *  or localStorage is unavailable. Also handles legacy value migration. */
export function getCurrentFormatMode(): FormatMode {
  if (typeof window === "undefined") return DEFAULT_MODE;
  try {
    return coerce(window.localStorage.getItem(LS_KEY));
  } catch {
    return DEFAULT_MODE;
  }
}

/** Segmented control — three modes side by side. */
export function FormatToggle() {
  const { mode, setMode } = useNumberFormat();
  const options: [FormatMode, string][] = [
    ["full", "Full"],
    ["l",    "L"],
    ["mn",   "Mn"],
  ];
  return (
    <div
      className="inline-flex items-stretch overflow-hidden rounded border border-rule text-xs font-mono"
      role="group"
      aria-label="₹ format"
    >
      {options.map(([m, label]) => {
        const active = mode === m;
        return (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={`px-2 py-1 transition ${
              active
                ? "bg-foreground text-background"
                : "text-mute hover:text-foreground"
            }`}
            title={`₹ format: ${label}`}
            aria-pressed={active}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
